import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { ThemeProvider, createTheme } from "@mui/material";
import { toast } from "react-toastify";
import api from "../../config/URL";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";
import {
  getColumnPreference,
  saveColumnPreference,
} from "../../utility/mrtColumnPersistence";

const MODULE_KEY = "ENROLLMENTREPORT";

const StudentEnrollmentReport = ({ selectedCenter }) => {
  /* -------------------- FILTER STATE -------------------- */
  const [filters, setFilters] = useState({
    centerId: selectedCenter || "",
    courseId: "",
    levelId: "",
    studentName: "",
    startDate: "",
    endDate: "",
  });

  const [tableData, setTableData] = useState([]);
  const [totalEnrollments, setTotalEnrollments] = useState(0);
  const [loading, setLoading] = useState(false);
  const [courseData, setCourseData] = useState(null);
  const defaultColumnVisibility = {};

  const [columnVisibility, setColumnVisibility] = useState(() =>
    getColumnPreference(MODULE_KEY, defaultColumnVisibility),
  );

  /* -------------------- API CALL -------------------- */
  const fetchEnrollmentReport = async (filterValues = filters) => {
    if (!selectedCenter) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("centerId", selectedCenter);

      if (filterValues.courseId)
        params.append("courseId", filterValues.courseId);

      if (filterValues.levelId) params.append("levelId", filterValues.levelId);

      if (filterValues.studentName)
        params.append("studentName", filterValues.studentName);

      if (filterValues.startDate)
        params.append("startDate", filterValues.startDate);

      if (filterValues.endDate) params.append("endDate", filterValues.endDate);

      const response = await api.get(
        `getEnrollmentReport?${params.toString()}`,
      );

      setTableData(response.data?.records || []);
      setTotalEnrollments(response.data?.totalEnrollments || 0);
    } catch (error) {
      toast.error("Failed to fetch student enrollment report");
      setTableData([]);
      setTotalEnrollments(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchListWithCenterIdData = async (centerId) => {
    try {
      const [courseDatas] = await Promise.all([
        fetchAllCoursesWithIdsC(centerId),
      ]);
      setCourseData(courseDatas || []);
    } catch (error) {
      toast.error("Failed to fetch center-specific data");
      console.error(error);
    }
  };

  /* -------------------- SYNC CENTER -------------------- */
  useEffect(() => {
    if (selectedCenter) {
      setFilters((prev) => ({ ...prev, centerId: selectedCenter }));
      fetchEnrollmentReport({
        ...filters,
        centerId: selectedCenter,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCenter]);

  useEffect(() => {
    if (filters.centerId) {
      fetchListWithCenterIdData(filters.centerId);
    }
  }, [filters.centerId]);
  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(
    () => [
      {
        accessorFn: (_, index) => index + 1,
        id: "sno",
        header: "S.NO",
        size: 40,
      },
      {
        accessorKey: "studentName",
        header: "Student Name",
      },
      {
        accessorKey: "yearOfBirth",
        header: "YOB",
      },
      {
        accessorKey: "centerName",
        header: "Centre",
      },
      {
        accessorKey: "level",
        header: "Level",
      },
      {
        accessorKey: "courseName",
        header: "Course",
      },
      {
        accessorKey: "className",
        header: "Class Name",
      },
      {
        accessorKey: "enrollDate",
        header: "Enroll Date",
        Cell: ({ cell }) =>
          cell.getValue()
            ? new Date(cell.getValue()).toLocaleDateString("en-GB")
            : "--",
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: tableData,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: (updater) => {
      const newVisibility =
        typeof updater === "function" ? updater(columnVisibility) : updater;

      setColumnVisibility(newVisibility);
      saveColumnPreference(MODULE_KEY, newVisibility);
    },
    enableColumnActions: true,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
    },
  });

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });

  /* -------------------- HANDLERS -------------------- */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    fetchEnrollmentReport(filters);
  };

  const clearFilters = () => {
    const reset = {
      centerId: selectedCenter || "",
      courseId: "",
      levelId: "",
      studentName: "",
      startDate: "",
      endDate: "",
    };
    setFilters(reset);
    fetchEnrollmentReport(reset);
  };

  /* -------------------- UI -------------------- */
  return (
    <div className="container-fluid my-4 center">
      <ol className="breadcrumb my-3 px-2">
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>{" "}
          &gt;
        </li>
        <li className="breadcrumb-item active">Student Enrollment Report</li>
      </ol>

      <div className="card">
        <div
          className="p-1 mb-3 d-flex align-items-center"
          style={{ background: "#f5f7f9" }}
        >
          <span className="text-muted ms-2">
            This database shows{" "}
            <strong style={{ color: "#287f71" }}>
              Student Enrollment Report
            </strong>
          </span>
        </div>

        {/* SUMMARY */}
        <div className="px-3 mb-3">
          <h6>
            Total Enrollments: <strong>{totalEnrollments}</strong>
          </h6>
        </div>

        {/* FILTERS */}
        <div className="mb-3 px-2">
          <div className="individual_fliters d-lg-flex">
            <select
              className="form-select form-select-sm center_list"
              name="courseId"
              onChange={handleFilterChange}
              value={filters.courseId}
            >
              <option value="">Select Courses</option>
              {courseData?.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.courseNames}
                </option>
              ))}
            </select>

            <input
              className="form-control form-control-sm center_list ms-2"
              placeholder="Student Name"
              name="studentName"
              value={filters.studentName}
              onChange={handleFilterChange}
            />

            <input
              type="date"
              className="form-control form-control-sm center_list ms-2"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />

            <input
              type="date"
              className="form-control form-control-sm center_list ms-2"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />

            <button
              className="btn btn-sm text-white ms-2"
              style={{ background: "#eb862a" }}
              onClick={applyFilters}
            >
              Search
            </button>

            <button
              className="btn btn-sm btn-border ms-2"
              onClick={clearFilters}
            >
              Clear
            </button>
          </div>
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <ThemeProvider theme={theme}>
            <MaterialReactTable table={table} />
          </ThemeProvider>
        )}
      </div>
    </div>
  );
};

export default StudentEnrollmentReport;
